import { z } from 'zod';
import { CURRENCIES, type Currency } from '../money/money';

/**
 * Cash reconciliation per trip (gitaumoses4/chukua#117): what the driver was given for a trip against what the office accepted as spent,
 * per driver and currency. Derived from the driver ledger's entries for the trip; only the outcome (cash returned, or the remainder carried
 * on the ledger) is stored, as `trip_cash_settlements`.
 */

const MAX_NOTE_LENGTH = 200;

/**
 * `POST /trips/:id/cash-settlements`. One decision for one driver and currency: `returnedMinor` is the cash that came back (0 to carry the
 * whole balance), and whatever is left of the balance stays on the driver's ledger. `receivedById` is the office user who took the cash.
 */
export const tripCashSettlementInputSchema = z
  .object({
    driverId: z.uuid(),
    currency: z.enum(CURRENCIES),
    returnedMinor: z.number().int().min(0),
    receivedById: z.uuid().optional(),
    note: z.string().trim().min(1).max(MAX_NOTE_LENGTH).optional(),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type TripCashSettlementInput = z.infer<typeof tripCashSettlementInputSchema>;

/** `open` still needs a decision; `reconciled` had nothing to settle; `settled` and `carried` are the two recorded outcomes. */
export type TripCashStatus = 'open' | 'reconciled' | 'settled' | 'carried';

export interface TripCashSettlementView {
  id: string;
  driverId: string;
  currency: Currency;
  /** The trip's balance for this driver when it was settled. */
  balanceMinor: number;
  cashReturnedMinor: number;
  /** What stayed on the ledger: the balance less the cash returned. */
  carriedMinor: number;
  receivedBy: { id: string; name: string } | null;
  recordedBy: { id: string; name: string } | null;
  note: string | null;
  createdAt: string;
}

/** Positive balance: the driver owes the company. Negative: the company owes the driver. */
export interface TripCashFigure {
  driverId: string;
  driverName: string;
  currency: Currency;
  /** Advance and top-ups, net of corrections. */
  advanceMinor: number;
  receiptedSpendMinor: number;
  cashReturnedMinor: number;
  balanceMinor: number;
  direction: 'driver_owes' | 'company_owes' | 'settled';
  status: TripCashStatus;
}

export interface TripCashExpenseCounts {
  pending: number;
  approved: number;
  rejected: number;
}

export interface TripCashReconciliationView {
  tripId: string;
  expenses: TripCashExpenseCounts;
  figures: TripCashFigure[];
  settlements: TripCashSettlementView[];
  /** No expense pending and no figure open. */
  isReconciled: boolean;
}
